import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import noop from 'lib/function/noop'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { trackOfferPageSuggestedPackageViewPackageClick } from '../analytics'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'

const Root = styled.div`
  border: 1px solid ${props => props.theme.palette.highlight.primary.darkForeground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const ContentContainer = styled.div`
  flex: 1;
  container-type: inline-size;
`

const Content = styled.div`
  display: flex;
  padding: ${rem(20)};
  gap: ${rem(20)};
  flex-direction: column;

  @container (min-width: 410px) {
    flex-direction: row;
    align-items: stretch;
  }

  ${mediaQueryDown.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const DesktopImageContainer = styled.div`
  width: ${rem(182)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const MobileRoomImageWrap = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const DetailsColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const PricingColumn = styled(Group)`
  width: ${rem(170)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    width: 100%;
    align-self: stretch;
  }
`

interface Props {
  suggestedOption: App.OfferPackageOptionView
  rate: App.EnrichedPropertyRate
  message: string
  propertyTimezone: string
  onViewPackage?: () => void
  viewPackageFallback: () => void
}

function PropertySuggestedRatePreview(props: Props) {
  const {
    suggestedOption,
    rate,
    message,
    propertyTimezone,
    onViewPackage = noop,
    viewPackageFallback,
  } = props
  const intl = useIntl()
  const { duration, airport: originAirport } = useContext(OfferPageStateContext)
  const roomType = suggestedOption.package.roomType
  const roomImageId = roomType?.images?.[0]?.id ?? NO_IMAGE_AVAILABLE_ID
  const inclusions = useMemo(() => [
    ...rate.bonusInclusions,
    ...rate.bundleInclusions,
    ...rate.inclusions,
  ], [rate.bonusInclusions, rate.bundleInclusions, rate.inclusions])

  const scrollToRate = useCallback(() => {
    const rateIds = [
      rate.rateId,
      rate.service.type === 'lpc' ? rate.service.rateId : undefined,
    ].filter(Boolean)
    const rateTiles = [...document.querySelectorAll('[data-testid]')]
    const target = rateTiles.find(el =>
      rateIds.includes(el.getAttribute('data-testid') ?? '') &&
      el.checkVisibility?.(),
    )

    if (target) {
      target.scrollIntoView({ behavior: 'auto', block: 'center' })
      return
    }

    if (!roomType?.id) {
      viewPackageFallback()
      return
    }

    const anchors = document.querySelectorAll(`#${CSS.escape(roomType.id)}`)
    const fallbackTarget = [...anchors].find(el => el.checkVisibility?.())
    if (fallbackTarget) {
      fallbackTarget.scrollIntoView({ behavior: 'auto', block: 'center' })
    } else {
      viewPackageFallback()
    }
  }, [rate.rateId, rate.service, roomType?.id, viewPackageFallback])

  const scrollToPackage = useCallback(() => {
    onViewPackage()
    trackOfferPageSuggestedPackageViewPackageClick()
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToRate)
    })
  }, [onViewPackage, scrollToRate])

  const durationLabel = rate.includesFlights ?
      intl.formatMessage(
        {
          id: 'KJ1LhfrQnjLw_CjFQZEJ',
          defaultMessage: '{count, plural, one {# night} other {# nights}} + flights',
          description: 'Duration with flights for the suggested property rate preview',
        },
        { count: duration },
      ) :
      intl.formatMessage(
        {
          id: 't7K3d7mlCND80qHWWT3v',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Duration without flights for the suggested property rate preview',
        },
        { count: duration },
      )

  const canShowPrice = !rate.includesFlights ||
    (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)
  const noFlightsAvailableMessage = intl.formatMessage(
    {
      id: 'KCe0399qOgmK-1GZSQxq',
      defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
      description: 'Error message when no flights are available from the selected airport',
    },
    { airportName: originAirport?.name },
  )
  const flightsWithin24HoursMessage = intl.formatMessage({
    id: 'WwEhobNYMcqSErYgfeMM',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
    description: 'Error message when departure is too soon for online flight booking',
  })
  const flightsAdvanceBookingMessage = intl.formatMessage({
    id: '0j8ptAKnepQILSZ4qfTs',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Error message when departure date is too far in advance for flight booking',
  })
  const addFlightsMessage = intl.formatMessage({
    id: 'OrJCJ4isDFtPDzbv6XYB',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info message about adding flights to the hotel package at checkout',
  })

  if (!roomType) return null

  return <Root>
    <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{message}</MessageBanner>
    <Group direction="horizontal" verticalAlign="stretch">
      <DesktopImageContainer>
        <HeightFillingImage
          id={roomImageId}
          width={182}
          mobileWidth="182px"
          fit="center"
          quality="good"
          dpr={2}
        />
      </DesktopImageContainer>
      <ContentContainer>
        <Content>
          <DetailsColumn>
            <Group direction="vertical" gap={8}>
              <CSSBreakpoint max="mobile">
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <MobileRoomImageWrap>
                    <ResponsiveImage
                      id={roomImageId}
                      width={64}
                      height={64}
                      mobileWidth="64px"
                      aspectRatio="1"
                      fit="center"
                      quality="good"
                      dpr={2}
                    />
                  </MobileRoomImageWrap>
                  <BodyText variant="large" weight="bold">{roomType.name}</BodyText>
                </Group>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <BodyText variant="large" weight="bold">{roomType.name}</BodyText>
              </CSSBreakpoint>
              <PropertyCancellationPolicy
                propertyTimeZone={propertyTimezone}
                headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
                headlineCancellationPolicyStart={rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined}
                fullyRefundableUntil={rate.fullyRefundableUntil}
                cancellationPolicies={rate.cancellationPolicies}
                currency={rate.currency}
                shouldClarifyRefundIsHotelOnly={rate.includesFlights}
              />
              <OfferInclusionsList
                inclusions={inclusions}
                truncateAfter={2}
                size="small"
                onShowMoreClick={scrollToPackage}
                showMoreText={intl.formatMessage({
                  id: 'VRGMHiTU_HhnQD0wQlHt',
                  defaultMessage: 'View more inclusions',
                  description: 'Link text to view more inclusions in the suggested property rate preview',
                })}
              />
            </Group>
          </DetailsColumn>
          <PricingColumn direction="vertical" gap={16} verticalAlign="end">
            {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
            {canShowPrice && <Group direction="vertical" horizontalAlign="start">
              <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="M"
                price={rate.totals.displayPrice}
                memberPrice={rate.luxPlusTotals?.displayPrice}
                saleUnit="total"
                horizontalAlign="start"
              />
              <PriceRowTaxesAndFees />
            </Group>}
            {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <Group direction="vertical" gap={12}>
              {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="light-foreground-alt" variant="small">
                {noFlightsAvailableMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="light-foreground-alt" variant="small">
                {flightsWithin24HoursMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="light-foreground-alt" variant="small">
                {flightsAdvanceBookingMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 0 && <BodyText colour="light-foreground-alt" variant="small">
                {addFlightsMessage}
              </BodyText>}
            </Group>}
            <TextButton
              kind="primary"
              size="small"
              fit="mobile-full-width"
              onClick={scrollToPackage}
            >
              {intl.formatMessage({
                id: '7z4-Ngi2aAhJS1Y3B-TV',
                defaultMessage: 'View package',
                description: 'Button text to view package details in suggested option preview',
              })}
            </TextButton>
          </PricingColumn>
        </Content>
      </ContentContainer>
    </Group>
  </Root>
}

export default PropertySuggestedRatePreview
